import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  MenuItem,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import ItemsService from '../../common/Rest/Services/ItemService/ItemService';
import { GetBookRequestSortByEnum, GetItemsAvailabilityFilter, GetItemsRequestData } from '../../common/Rest/Services/ItemService/Data';
import { Item } from '../../common/Rest/Services/Data';
import AuthorsService, { Author } from '../../common/Rest/Services/AuthorsService';
import GenresService, { Genre } from '../../common/Rest/Services/GenresService';
import PublishersService, { Publisher } from '../../common/Rest/Services/PublishersService';
import TagsService, { Tag } from '../../common/Rest/Services/TagsService';
import FilterPanel from './FilterPanel';
import FilterChips from './FilterChips';
import ItemGrid from './ItemGrid';
import HomePageBanner from './HomePageBanner';
import './HomePage.css';
import { BookCollection } from '../../common/Models/Books';
import { getCollections } from '../../common/Rest/Services/BookService';
import BookCard from '../../BookCard/BookCard';

let cachedAuthors: Author[] | null = null;
let cachedGenres: Genre[] | null = null;
let cachedPublishers: Publisher[] | null = null;
let cachedTags: Tag[] | null = null;

const PAGE_SIZE = 12;

export interface Filters {
  authors: number[];
  genres: number[];
  publishers: number[];
  tags: number[];
  series: number[];
  availability: AvailabilityFilter;
}

export type AvailabilityFilter = 'all' | 'in' | 'out';
type IdFilterKey = 'authors' | 'genres' | 'publishers' | 'tags';

export const EMPTY_FILTERS: Filters = {
  authors: [],
  genres: [],
  publishers: [],
  tags: [],
  series: [],
  availability: 'all',
};

export const hasActiveFilters = (filters: Filters) =>
  filters.authors.length > 0 ||
  filters.genres.length > 0 ||
  filters.publishers.length > 0 ||
  filters.tags.length > 0 ||
  filters.series.length > 0 ||
  filters.availability !== 'all';

const filtersEqual = (a: Filters, b: Filters) => JSON.stringify(a) === JSON.stringify(b);

const readIdParam = (value: string | null) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const readAvailabilityParam = (value: string | null): AvailabilityFilter =>
  value === 'in' || value === 'out' ? value : 'all';

const toAvailabilityRequest = (value: AvailabilityFilter) => {
  if (value === 'in') return GetItemsAvailabilityFilter.InStock;
  if (value === 'out') return GetItemsAvailabilityFilter.OutOfStock;
  return GetItemsAvailabilityFilter.All;
};

function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [asideOpen, setAsideOpen] = useState(false);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [collections, setCollections] = useState<BookCollection[]>([]);
  const searchQuery = (searchParams.get('q') ?? '').trim();
  const sortOrder = searchParams.get('sort') === 'alphabetical' ? 'alphabetical' : 'newest';
  const activeSearchQuery = searchQuery.length >= 3 ? searchQuery : undefined;
  const [appliedFilters, setAppliedFilters] = useState<Filters>({
    authors: readIdParam(searchParams.get('author')) ? [readIdParam(searchParams.get('author'))!] : [],
    genres: readIdParam(searchParams.get('genre')) ? [readIdParam(searchParams.get('genre'))!] : [],
    publishers: readIdParam(searchParams.get('publisher')) ? [readIdParam(searchParams.get('publisher'))!] : [],
    tags: readIdParam(searchParams.get('tag')) ? [readIdParam(searchParams.get('tag'))!] : [],
    series: readIdParam(searchParams.get('series')) ? [readIdParam(searchParams.get('series'))!] : [],
    availability: readAvailabilityParam(searchParams.get('availability')),
  });
  const [seriesLabel, setSeriesLabel] = useState<string | undefined>(searchParams.get('seriesName') ?? undefined);
  const [pendingFilters, setPendingFilters] = useState<Filters>(appliedFilters);

  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmall = useMediaQuery('(min-width:600px) and (max-width:959px)');
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const gridColumns = isExtraSmall ? 1 : isSmall ? 2 : 3;
  const fetchIdRef = useRef(0);
  const fetchAbortRef = useRef<AbortController | null>(null);
  const searchFetchRef = useRef<string | undefined>(activeSearchQuery);
  const sortFetchRef = useRef(sortOrder);
  const lastItemRef = useRef<HTMLLIElement | null>(null);
  const pendingFiltersRef = useRef<Filters>(pendingFilters);
  const appliedFiltersRef = useRef<Filters>(appliedFilters);
  const allowPaginationRef = useRef(false);

  const updateUrl = (filters: Filters) => {
    const params = new URLSearchParams();
    params.set('sort', sortOrder);
    if (activeSearchQuery) params.set('q', activeSearchQuery);
    filters.authors.forEach((id) => params.append('author', String(id)));
    filters.genres.forEach((id) => params.append('genre', String(id)));
    filters.publishers.forEach((id) => params.append('publisher', String(id)));
    filters.tags.forEach((id) => params.append('tag', String(id)));
    filters.series.forEach((id) => params.append('series', String(id)));
    if (filters.series.length && seriesLabel) params.set('seriesName', seriesLabel);
    if (filters.availability !== 'all') params.set('availability', filters.availability);
    setSearchParams(params, { replace: true });
  };

  // Commits a filter set: makes it the one driving the fetch/URL, and keeps the
  // draft (pendingFilters) in sync with it so the panel never shows a stale draft.
  const applyFilters = (newFilters: Filters) => {
    allowPaginationRef.current = false;
    window.scrollTo({ top: 0 });
    pendingFiltersRef.current = newFilters;
    setPendingFilters(newFilters);
    appliedFiltersRef.current = newFilters;
    setAppliedFilters(newFilters);
    setPage(1);
    setHasMore(true);
    updateUrl(newFilters);
  };

  // Updates only the draft shown in the filter panel; does not touch the
  // applied filters or trigger a fetch until the user hits "Apply".
  const updatePendingFilters = (newFilters: Filters) => {
    pendingFiltersRef.current = newFilters;
    setPendingFilters(newFilters);
  };

  const handleApplyFilters = () => {
    applyFilters(pendingFiltersRef.current);
  };

  const handleAuthorChange = (author: Author) => {
    const currentFilters = pendingFiltersRef.current;
    const nextAuthors = currentFilters.authors.includes(author.id)
      ? currentFilters.authors.filter((id) => id !== author.id)
      : [...currentFilters.authors, author.id];
    updatePendingFilters({ ...currentFilters, authors: nextAuthors });
  };

  const handlePublisherChange = (publisher: Publisher) => {
    const currentFilters = pendingFiltersRef.current;
    const nextPublishers = currentFilters.publishers.includes(publisher.id)
      ? currentFilters.publishers.filter((id) => id !== publisher.id)
      : [...currentFilters.publishers, publisher.id];
    updatePendingFilters({ ...currentFilters, publishers: nextPublishers });
  };

  const handleGenreChange = (genre: Genre) => {
    const currentFilters = pendingFiltersRef.current;
    const nextGenres = currentFilters.genres.includes(genre.id)
      ? currentFilters.genres.filter((id) => id !== genre.id)
      : [...currentFilters.genres, genre.id];
    updatePendingFilters({ ...currentFilters, genres: nextGenres });
  };

  const handleAvailabilityChange = (availability: AvailabilityFilter) => {
    updatePendingFilters({ ...pendingFiltersRef.current, availability });
  };

  // Chip removal, series clear, and reset act on the currently-applied filters
  // (discarding any unapplied draft edits), and take effect immediately instead
  // of waiting for "Apply".
  const handleChipDelete = (type: IdFilterKey, id: number) => {
    const currentFilters = appliedFiltersRef.current;
    applyFilters({
      ...currentFilters,
      [type]: currentFilters[type].filter((value) => value !== id),
    });
  };

  const handleAvailabilityClear = () => {
    applyFilters({ ...appliedFiltersRef.current, availability: 'all' });
  };

  const handleSeriesClear = () => {
    setSeriesLabel(undefined);
    applyFilters({ ...appliedFiltersRef.current, series: [] });
  };

  const handleResetFilters = () => {
    setSeriesLabel(undefined);
    applyFilters(EMPTY_FILTERS);
  };

  const fetchItems = useCallback(async () => {
    if ((activeSearchQuery !== searchFetchRef.current || sortOrder !== sortFetchRef.current) && page !== 1) return;

    // A previous request that's still in flight when filters change again must never be
    // allowed to resolve and win the race against the newer one — the fetchIdRef check below
    // only ignores its result, but aborting it outright is what actually stops it competing.
    fetchAbortRef.current?.abort();
    const abortController = new AbortController();
    fetchAbortRef.current = abortController;

    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    setLoading(true);

    try {
      const requestData: GetItemsRequestData = {
        search: activeSearchQuery,
        tags: appliedFilters.tags.length ? appliedFilters.tags : undefined,
        authors: appliedFilters.authors.length ? appliedFilters.authors : undefined,
        genres: appliedFilters.genres.length ? appliedFilters.genres : undefined,
        publishers: appliedFilters.publishers.length ? appliedFilters.publishers : undefined,
        series: appliedFilters.series.length ? appliedFilters.series : undefined,
        onlyInStock: undefined,
        availability: toAvailabilityRequest(appliedFilters.availability),
        pageIndex: page - 1,
        pageCount: PAGE_SIZE,
        sortBy: sortOrder === 'newest' ? GetBookRequestSortByEnum.Id : GetBookRequestSortByEnum.Name,
        sortDescending: sortOrder === 'newest',
      };

      const newItems = await ItemsService.GetItems(requestData, abortController.signal);
      if (fetchIdRef.current !== currentFetchId) return;
      searchFetchRef.current = activeSearchQuery;
      sortFetchRef.current = sortOrder;
      setHasMore(newItems.length === PAGE_SIZE);
      setItems((previous) => (page === 1 ? newItems : [...previous, ...newItems]));
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error('Error fetching books:', error);
    } finally {
      if (fetchIdRef.current === currentFetchId) {
        setLoading(false);
      }
    }
  }, [activeSearchQuery, appliedFilters, page, sortOrder]);

  useEffect(() => {
    allowPaginationRef.current = false;
    window.scrollTo({ top: 0 });
    setPage(1);
    setHasMore(true);
  }, [activeSearchQuery, sortOrder]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        allowPaginationRef.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    getCollections().then(setCollections).catch(() => setCollections([]));
  }, []);

  useEffect(() => {
    if (loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && allowPaginationRef.current) {
          setPage((previous) => previous + 1);
        }
      },
      { rootMargin: '520px 0px', threshold: 0.1 }
    );

    const currentElement = lastItemRef.current;
    if (currentElement) observer.observe(currentElement);
    return () => observer.disconnect();
  }, [loading, hasMore]);

  useEffect(() => {
    const fetchFilterData = async () => {
      if (cachedAuthors && cachedGenres && cachedPublishers && cachedTags) {
        setAuthors(cachedAuthors);
        setGenres(cachedGenres);
        setPublishers(cachedPublishers);
        setTags(cachedTags);
        return;
      }

      try {
        const [authorsData, genresData, publishersData, tagsData] = await Promise.all([
          AuthorsService.GetFiltered(),
          GenresService.GetFiltered(),
          PublishersService.GetFiltered(),
          TagsService.GetFiltered(),
        ]);
        cachedAuthors = authorsData;
        cachedGenres = genresData;
        cachedPublishers = publishersData;
        cachedTags = tagsData;
        setAuthors(authorsData);
        setGenres(genresData);
        setPublishers(publishersData);
        setTags(tagsData);
      } catch (error) {
        console.error('Error fetching filters:', error);
      }
    };

    fetchFilterData();
  }, []);

  const fetchAuthors = useCallback(
    async (search: string) => {
      if (!search.trim()) return authors;
      const currentFilters = pendingFiltersRef.current;
      const data = await AuthorsService.GetFiltered(search, currentFilters.publishers);
      const selected = authors.filter((author) => currentFilters.authors.includes(author.id));
      return [...selected, ...data.filter((author) => !selected.some((item) => item.id === author.id))];
    },
    [authors]
  );

  const fetchPublishers = useCallback(
    async (search: string) => {
      if (!search.trim()) return publishers;
      const currentFilters = pendingFiltersRef.current;
      const data = await PublishersService.GetFiltered(search, currentFilters.authors);
      const selected = publishers.filter((publisher) => currentFilters.publishers.includes(publisher.id));
      return [...selected, ...data.filter((publisher) => !selected.some((item) => item.id === publisher.id))];
    },
    [publishers]
  );

  const fetchGenres = useCallback(
    async (search: string) => {
      if (!search.trim()) return genres;
      return await GenresService.GetFiltered(search);
    },
    [genres]
  );

  return (
    <div className="home-page">
      <HomePageBanner />

      {collections.length > 0 && (
        <section className="collections-shell default-container">
          {collections.map((collection) => (
            <article className="collection-section" key={collection.id}>
              <div className="collection-heading">
                <p>{collection.description}</p>
                <h2>{collection.title}</h2>
              </div>
              <div className="collection-row">
                {collection.books.slice(0, 5).map((book) => (
                  <BookCard key={book.id} book={book} />
                ))}
              </div>
            </article>
          ))}
        </section>
      )}

      <div className="main-container">
        <div className="default-container d-flex">
          {isLarge && (
            <aside className="aside-search" style={{ width: 300 }}>
              <FilterPanel
                filters={pendingFilters}
                authors={authors}
                genres={genres}
                publishers={publishers}
                fetchAuthors={fetchAuthors}
                fetchGenres={fetchGenres}
                fetchPublishers={fetchPublishers}
                onAuthorChange={handleAuthorChange}
                onGenreChange={handleGenreChange}
                onPublisherChange={handlePublisherChange}
                onAvailabilityChange={handleAvailabilityChange}
                onResetFilters={handleResetFilters}
                onApply={handleApplyFilters}
                hasPendingChanges={!filtersEqual(pendingFilters, appliedFilters)}
              />
            </aside>
          )}

          <main style={{ flex: 1, padding: 16, position: 'relative' }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              {!isLarge && (
                <Button variant="contained" onClick={() => setAsideOpen(true)}>
                  Фільтри
                </Button>
              )}
              <TextField
                select
                SelectProps={{ inputProps: { 'aria-label': 'Сортування' } }}
                size="small"
                value={sortOrder}
                onChange={(event) => {
                  allowPaginationRef.current = false;
                  setPage(1);
                  setHasMore(true);
                  const params = new URLSearchParams(searchParams);
                  params.set('sort', event.target.value);
                  setSearchParams(params, { replace: true });
                }}
                sx={{ minWidth: 200, ml: 'auto' }}
              >
                <MenuItem value="newest">Найновіші</MenuItem>
                <MenuItem value="alphabetical">За алфавітом</MenuItem>
              </TextField>
            </Box>

            <FilterChips
              appliedFilters={appliedFilters}
              authors={authors}
              genres={genres}
              publishers={publishers}
              tags={tags}
              seriesLabel={seriesLabel}
              onChipDelete={handleChipDelete}
              onAvailabilityClear={handleAvailabilityClear}
              onSeriesClear={handleSeriesClear}
            />

            <div className={`items-container ${loading && page === 1 && items.length > 0 ? 'items-container-refreshing' : ''}`}>
              {items.length > 0 ? (
                <ItemGrid items={items} gridColumns={gridColumns} lastItemRef={lastItemRef} hasSidebar={isLarge} />
              ) : (
                !loading && <div className="catalog-empty">На жаль, за вашим запитом нічого не знайдено</div>
              )}
              {loading && items.length === 0 && <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 2 }} />}
              {loading && page === 1 && items.length > 0 && (
                <div className="catalog-refresh-indicator">Оновлюємо добірку...</div>
              )}
            </div>

            {!isLarge && (
              <Drawer keepMounted open={asideOpen} onClose={() => setAsideOpen(false)} anchor="left" transitionDuration={250}>
                <Box
                  sx={{
                    width: isExtraSmall ? '100vw' : 320,
                    height: '100vh',
                    padding: 0,
                    position: 'relative',
                    backgroundColor: 'background.paper',
                  }}
                >
                  <IconButton
                    aria-label="Закрити фільтри"
                    onClick={() => setAsideOpen(false)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      // FilterPanel's own header is position:relative with zIndex:1 and spans
                      // the full width right up to this corner, so without a higher zIndex here
                      // it silently paints over this button — visible, but unclickable.
                      zIndex: 2,
                      bgcolor: 'action.selected',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                  <Box sx={{ height: '100%' }}>
                    <FilterPanel
                      filters={pendingFilters}
                      authors={authors}
                      genres={genres}
                      publishers={publishers}
                      fetchAuthors={fetchAuthors}
                      fetchGenres={fetchGenres}
                      fetchPublishers={fetchPublishers}
                      onAuthorChange={handleAuthorChange}
                      onGenreChange={handleGenreChange}
                      onPublisherChange={handlePublisherChange}
                      onAvailabilityChange={handleAvailabilityChange}
                      onResetFilters={handleResetFilters}
                      onApply={() => {
                        handleApplyFilters();
                        setAsideOpen(false);
                      }}
                      hasPendingChanges={!filtersEqual(pendingFilters, appliedFilters)}
                    />
                  </Box>
                </Box>
              </Drawer>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
